// Phase 3 — client dashboard split into two lanes: what the client's team must
// do (responsible_party client/shared, actionable) vs what Priority Risk is
// doing (pr, status-only). Plus a KPI strip bound to those sets + project health.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CheckSquare, FileText, Inbox, Activity, ChevronDown, ArrowUpRight,
    CircleCheck, Clock, ThumbsUp,
} from "lucide-react";
import Pill from "@/components/Pill";
import { STATUS_PILL } from "@/lib/constants";

const PR_STATUS = {
    green: { label: "On track", tone: "green" },
    amber: { label: "Needs attention", tone: "amber" },
    red: { label: "At risk", tone: "red" },
};

function fmtDue(s) {
    if (!s) return null;
    try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
    catch { return s; }
}

function KpiMini({ testid, icon: Icon, label, value, brand }) {
    return (
        <div data-testid={testid} className="bg-white border rounded-[11px] px-4 py-3.5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon className="w-3.5 h-3.5" style={{ color: brand }} />
                <span className="text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: "#9A968F" }}>{label}</span>
            </div>
            <div className="text-[26px] font-bold leading-none text-[#1F1F1F]">{value}</div>
        </div>
    );
}

export default function ClientDashboardLanes({ data, brand }) {
    const navigate = useNavigate();
    const [showReasons, setShowReasons] = useState(false);
    const [openLanes, setOpenLanes] = useState(true);
    const k = data.kpis;
    const tasks = data.your_team_tasks || [];
    const prWork = data.pr_work || [];
    const prStatus = PR_STATUS[k.pr_status] || PR_STATUS.green;
    const reasons = data.pr_status_reasons || [];

    const openTask = (t) => {
        if (t.type === "task" && t.project_id) navigate(`/app/projects/${t.project_id}`);
        else if (t.type === "action") navigate(`/app/actions?open=${t.id}`);
    };

    return (
        <div data-testid="portal-lanes" className="space-y-5">
            {/* KPI strip */}
            <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))" }}>
                <KpiMini testid="kpi-needs-action" icon={CheckSquare} label="Needs your action" value={k.needs_your_action} brand={brand} />
                <KpiMini testid="kpi-reports-review" icon={FileText} label="Reports to review" value={k.reports_to_review} brand={brand} />
                <KpiMini testid="kpi-docs-requested" icon={Inbox} label="Documents requested from you" value={k.documents_requested} brand={brand} />
                {/* Good practice this month — positive observations, feel-good metric */}
                <button data-testid="kpi-good-practice" onClick={() => navigate("/app/incidents")}
                    className="text-left bg-white border rounded-[11px] px-4 py-3.5 shadow-brand-sm transition-colors hover:bg-[#F5FAF4]"
                    style={{ borderColor: "#CDE6C9" }}>
                    <div className="flex items-center gap-2 mb-1.5">
                        <ThumbsUp className="w-3.5 h-3.5" style={{ color: "#3F7D3A" }} />
                        <span className="text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: "#3F7D3A" }}>Good practice this month</span>
                    </div>
                    <div className="text-[26px] font-bold leading-none" style={{ color: "#2F5E2B" }}>{k.good_practice_month ?? 0}</div>
                </button>
                {/* PR status — bound to project health, not a manual value */}
                <div data-testid="kpi-pr-status" className="bg-white border rounded-[11px] px-4 py-3.5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <div className="flex items-center gap-2 mb-1.5">
                        <Activity className="w-3.5 h-3.5" style={{ color: brand }} />
                        <span className="text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: "#9A968F" }}>Priority Risk status</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_PILL[prStatus.tone].fg }} />
                        <span className="text-[16px] font-bold text-[#1F1F1F]">{prStatus.label}</span>
                        {reasons.length > 0 && (
                            <button data-testid="pr-status-why" onClick={() => setShowReasons((v) => !v)}
                                className="ml-auto text-[10.5px] font-semibold flex items-center gap-0.5 hover:underline" style={{ color: brand }}>
                                Why? <ChevronDown className={`w-3 h-3 transition-transform ${showReasons ? "rotate-180" : ""}`} />
                            </button>
                        )}
                    </div>
                    {showReasons && reasons.length > 0 && (
                        <ul data-testid="pr-status-reasons" className="mt-2 pt-2 border-t space-y-1" style={{ borderColor: "#F0EDE8" }}>
                            {reasons.map((r, i) => (
                                <li key={i} className="text-[10.5px] text-[#6F6A64] leading-snug">• {r}</li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Two lanes */}
            <div className="grid lg:grid-cols-2 gap-5">
                {/* Your team's tasks — primary CTA */}
                <div data-testid="your-tasks-lane" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: brand, borderWidth: 1.5 }}>
                    <button data-testid="your-tasks-toggle" onClick={() => setOpenLanes((v) => !v)}
                        className="w-full px-5 py-3.5 flex items-center gap-2 text-left" style={{ borderBottom: openLanes ? "1px solid #F0EDE8" : "none", background: `${brand}0d` }}>
                        <CheckSquare className="w-4 h-4" style={{ color: brand }} />
                        <h3 className="text-[14px] font-semibold text-[#1F1F1F] flex-1">Your team's tasks</h3>
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: brand }}>{tasks.length}</span>
                        <ChevronDown className={`w-4 h-4 text-[#9A968F] transition-transform ${openLanes ? "rotate-180" : ""}`} />
                    </button>
                    {openLanes && (
                    <ul>
                        {tasks.map((t) => (
                            <li key={`${t.type}-${t.id}`} data-testid={`your-task-${t.id}`} onClick={() => openTask(t)}
                                className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{t.title}</div>
                                    <div className="text-[10.5px] text-[#9A968F] truncate flex items-center gap-1.5 mt-0.5">
                                        {t.type === "task" ? "Project task" : "Action"}
                                        {t.project_name ? <> · {t.project_name}</> : null}
                                    </div>
                                </div>
                                {t.due_date && (
                                    <Pill tone={t.overdue ? "red" : "neutral"}>
                                        <Clock className="w-3 h-3 inline mr-0.5" />{t.overdue ? "Overdue" : fmtDue(t.due_date)}
                                    </Pill>
                                )}
                                <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brand }} />
                            </li>
                        ))}
                        {tasks.length === 0 && (
                            <li className="px-5 py-8 text-center">
                                <CircleCheck className="w-7 h-7 mx-auto mb-2" style={{ color: "#5FA35A" }} />
                                <div className="text-[12.5px] font-semibold text-[#1F1F1F]">You're all caught up</div>
                                <div className="text-[11px] text-[#9A968F] mt-0.5">No tasks need your team right now.</div>
                            </li>
                        )}
                    </ul>
                    )}
                </div>

                {/* What Priority Risk is doing — read-only reassurance */}
                <div data-testid="pr-work-lane" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    <button data-testid="pr-work-toggle" onClick={() => setOpenLanes((v) => !v)}
                        className="w-full px-5 py-3.5 flex items-center gap-2 text-left" style={{ borderBottom: openLanes ? "1px solid #F0EDE8" : "none" }}>
                        <Activity className="w-4 h-4" style={{ color: "#6F6A64" }} />
                        <h3 className="text-[14px] font-semibold text-[#1F1F1F] flex-1">What Priority Risk is doing</h3>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>For visibility</span>
                        <ChevronDown className={`w-4 h-4 text-[#9A968F] transition-transform ${openLanes ? "rotate-180" : ""}`} />
                    </button>
                    {openLanes && (
                    <ul>
                        {prWork.map((w) => (
                            <li key={`${w.type}-${w.id}`} data-testid={`pr-work-${w.id}`}
                                className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{w.title}</div>
                                    <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">
                                        {w.type === "report" ? "Report" : "Action"}
                                        {w.project_name ? <> · {w.project_name}</> : null}
                                    </div>
                                </div>
                                <Pill tone="neutral">{w.status_label}</Pill>
                            </li>
                        ))}
                        {prWork.length === 0 && (
                            <li className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">
                                Nothing in progress from Priority Risk right now.
                            </li>
                        )}
                    </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
